import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertTriangle, Bell, CheckCheck, Hexagon, Radio, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { notificationNavigation } from "@/lib/notification-link";
import { useAuth } from "@/lib/auth";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import type { NotificationRow } from "@/components/NotificationsBell";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({ meta: [{ title: "Notifications — MissionReady" }] }),
  component: NotificationsPage,
});

const SOURCES = [
  { key: "all", label: "All", Icon: Bell },
  { key: "incident", label: "Incidents", Icon: AlertTriangle },
  { key: "dispatch", label: "Dispatch", Icon: Radio },
  { key: "geofence", label: "Geofence", Icon: Hexagon },
] as const;

const sevColor = (sev: string) =>
  sev === "critical" ? "text-destructive"
  : sev === "warning" ? "text-warning"
  : "text-primary";

const sourceIcon = (source: string) => {
  switch (source) {
    case "incident": return AlertTriangle;
    case "dispatch": return Radio;
    case "geofence": return Hexagon;
    default: return Bell;
  }
};

function NotificationsPage() {
  const { user, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !currentOrg) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("organization_id", currentOrg.id)
        .eq("recipient_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (!cancelled) {
        setItems((data ?? []) as NotificationRow[]);
        setLoading(false);
      }
    };
    load();

    const channel = supabase
      .channel(`notif-page-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `recipient_id=eq.${user.id}` },
        () => load(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user, currentOrg]);

  const filtered = items.filter((n) => {
    if (filter !== "all" && n.source !== filter) return false;
    if (showUnreadOnly && n.read_at) return false;
    return true;
  });

  const unreadCount = items.filter((n) => !n.read_at).length;

  const markRead = async (id: number) => {
    setItems((p) => p.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  };

  const markAllRead = async () => {
    if (!user || !currentOrg) return;
    const now = new Date().toISOString();
    setItems((p) => p.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    await supabase
      .from("notifications")
      .update({ read_at: now })
      .eq("recipient_id", user.id)
      .eq("organization_id", currentOrg.id)
      .is("read_at", null);
  };

  const remove = async (id: number) => {
    setItems((p) => p.filter((n) => n.id !== id));
    await supabase.from("notifications").delete().eq("id", id);
  };

  const open = async (n: NotificationRow) => {
    if (!n.read_at) await markRead(n.id);
    if (n.link) navigate(notificationNavigation(n.link) as never);
  };

  return (
    <PageContainer className="py-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showUnreadOnly}
              onChange={(e) => setShowUnreadOnly(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Unread only
          </label>
          <Button variant="outline" size="sm" onClick={markAllRead} disabled={unreadCount === 0}>
            <CheckCheck className="mr-1.5 h-4 w-4" /> Mark all read
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {SOURCES.map(({ key, label, Icon }) => {
          const active = filter === key;
          const count = key === "all" ? items.length : items.filter((n) => n.source === key).length;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              <span className="text-xs opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            No notifications match this view.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((n) => {
              const Icon = sourceIcon(n.source);
              return (
                <li key={n.id} className={`group flex items-start gap-4 px-4 py-3 ${!n.read_at ? "bg-primary/5" : ""}`}>
                  <div className={`mt-0.5 ${sevColor(n.severity)}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <button
                    onClick={() => open(n)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <p className="font-medium">{n.title}</p>
                      <span className="text-xs text-muted-foreground">
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                      {!n.read_at && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    {n.event_type === "broadcast" && (
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                        Broadcast
                        {(n.metadata as { sender_name?: string } | null)?.sender_name
                          ? ` · From ${(n.metadata as { sender_name?: string }).sender_name}`
                          : ""}
                        {" · "}
                        {(n.metadata as { scope?: string; site_name?: string } | null)?.scope ===
                        "site"
                          ? `Site: ${(n.metadata as { site_name?: string }).site_name ?? "Assigned site"}`
                          : "Scope: All Sites"}
                      </p>
                    )}
                    {n.body && <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>}
                  </button>
                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {!n.read_at && (
                      <Button variant="ghost" size="sm" onClick={() => markRead(n.id)}>
                        Mark read
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(n.id)} aria-label="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Notifications fire from <Link to="/incidents" className="text-primary hover:underline">incidents</Link>,{" "}
        <Link to="/console/dispatch" className="text-primary hover:underline">dispatch</Link>, and{" "}
        <Link to="/geofences" className="text-primary hover:underline">geofence</Link> events for your organization.
      </p>
    </PageContainer>
  );
}
